/**
 * Adapter for Nearley parser to match the interface of the old hand-written parser
 */

import nearley from "nearley";
import { StmtNS } from "../ast-types";
import pythonLexer from "./lexer";
import { ParseError } from "./parse-error";
import grammar from "./python-grammar";

export { ParseError };

/**
 * NearleyParser - Drop-in replacement for the old Parser class
 */
export class NearleyParser {
  private readonly source: string;

  constructor(source: string, _tokens?: unknown[]) {
    // Note: Nearley doesn't use pre-tokenized input in the same way
    // The lexer is integrated into the parser
    this.source = source;
  }

  /**
   * Parse the source code and return the AST
   */
  parse(): StmtNS.FileInput {
    // Create a new parser instance with our grammar
    const parser = new nearley.Parser(
      nearley.Grammar.fromCompiled({
        ...(grammar as unknown as nearley.CompiledRules),
        Lexer: pythonLexer,
      }),
    );

    try {
      // Feed the source code to the parser
      parser.feed(this.source);

      // Check if we got results
      if (parser.results.length === 0) {
        throw new Error("Unexpected end of input - no parse results");
      }

      // Ambiguous grammar is a bug — fail loudly so tests catch it
      if (parser.results.length > 1) {
        throw new Error(`Ambiguous grammar: ${parser.results.length} possible parses for input`);
      }
      // Return the first (or only) parse result
      return parser.results[0];
    } catch (error: unknown) {
      // Transform Nearley errors to match our error format
      const err = error as {
        token?: { value?: string; type?: string; line?: number; col?: number };
      };
      if (error instanceof ParseError) {
        // Raised by a rule of the grammar, which does not have the source.
        if (!error.source) error.source = this.source;
        throw error;
      }
      if (err.token) {
        const token = err.token;
        const line = token.line || 0;
        const col = token.col || 0;
        throw new ParseError(
          `Unexpected token: ${token.value || token.type} at line ${line}, column ${col}`,
          line,
          col,
          this.source,
        );
      }
      throw error;
    }
  }
}

/**
 * Convenience function to parse Python source code
 */
export function parse(source: string): StmtNS.FileInput {
  const parser = new NearleyParser(source);
  return parser.parse();
}
